"use client";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/radix/tooltip";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface CarouselNavigationProps {
	currentIndex: number;
	totalItems: number;
	onPrevious: () => void;
	onNext: () => void;
	onGoToIndex: (index: number) => void;
	disabled?: boolean;
	maxVisibleDots?: number; // Maximum number of dots to show before tapering
}

export function CarouselNavigation({
	currentIndex,
	totalItems,
	onPrevious,
	onNext,
	onGoToIndex,
	disabled = false,
	maxVisibleDots = 7, // Default to showing 7 dots max
}: CarouselNavigationProps) {
	// Don't render if there's only one item
	if (totalItems <= 1) {
		return null;
	}

	// Calculate which dots to show and their properties
	const getDotProperties = () => {
		if (totalItems <= maxVisibleDots) {
			// Show all dots normally if we have few items
			return Array.from({ length: totalItems }, (_, index) => ({
				index,
				opacity: 1,
				size: "w-2 h-2",
				isVisible: true,
			}));
		}

		// For many items, show a subset with tapering
		const dots = [];
		const sideCount = Math.floor((maxVisibleDots - 1) / 2); // How many dots on each side of current

		// Calculate the range of dots to show
		let startIndex = Math.max(0, currentIndex - sideCount);
		let endIndex = Math.min(totalItems - 1, currentIndex + sideCount);

		// Adjust if we're near the beginning or end
		if (currentIndex < sideCount) {
			endIndex = Math.min(totalItems - 1, maxVisibleDots - 1);
		} else if (currentIndex > totalItems - sideCount - 1) {
			startIndex = Math.max(0, totalItems - maxVisibleDots);
		}

		// Create dots with tapering effects
		for (let i = startIndex; i <= endIndex; i++) {
			const distanceFromCurrent = Math.abs(i - currentIndex);
			const maxDistance = Math.max(currentIndex - startIndex, endIndex - currentIndex);

			let opacity = 1;
			let size = "w-2 h-2";

			if (maxDistance > 0) {
				// Calculate tapering based on distance from current
				const taperFactor = 1 - distanceFromCurrent / (maxDistance + 1);
				opacity = Math.max(0.3, taperFactor);

				// Smaller dots for items further away
				if (distanceFromCurrent > 2) {
					size = "w-1.5 h-1.5";
				} else if (distanceFromCurrent > 1) {
					size = "w-1.5 h-1.5";
				}
			}

			dots.push({
				index: i,
				opacity,
				size,
				isVisible: true,
			});
		}

		// Add indicators for more items if needed
		if (startIndex > 0) {
			dots.unshift({
				index: -1, // Special index for "more left" indicator
				opacity: 0.3,
				size: "w-1 h-1",
				isVisible: true,
			});
		}

		if (endIndex < totalItems - 1) {
			dots.push({
				index: -2, // Special index for "more right" indicator
				opacity: 0.3,
				size: "w-1 h-1",
				isVisible: true,
			});
		}

		return dots;
	};

	const dotProperties = getDotProperties();

	return (
		<div className="flex items-center justify-between px-2 py-2 bg-gray-50 border-b">
			<Tooltip>
				<TooltipTrigger asChild>
					<button
						onClick={onPrevious}
						type="button"
						aria-label="Previous"
						className="p-2 hover:bg-gray-200 rounded-full cursor-pointer transition-colors"
						disabled={disabled}
					>
						<ChevronLeft className="w-4 h-4" aria-hidden="true" />
					</button>
				</TooltipTrigger>
				<TooltipContent>
					<p>Previous</p>
				</TooltipContent>
			</Tooltip>

			{/* Dots indicator with tapering */}
			<div className="flex flex-nowrap flex-1 mx-4 justify-center">
				<div className="flex flex-nowrap gap-1 items-center">
					{dotProperties.map((dot, _arrayIndex) => {
						// Regular dots
						return (
							<button
								type="button"
								title={`Go to item ${dot.index + 1}`}
								aria-label={`Go to item ${dot.index + 1}`}
								key={dot.index}
								onClick={() => onGoToIndex(dot.index)}
								className={`${dot.size} rounded-full transition-all duration-200 ${
									dot.index === currentIndex ? "bg-primary" : "bg-gray-300"
								}`}
								style={{ opacity: dot.opacity }}
								disabled={disabled}
							/>
						);
					})}
				</div>
			</div>

			<Tooltip>
				<TooltipTrigger asChild>
					<button
						type="button"
						aria-label="Next"
						onClick={onNext}
						className="p-2 hover:bg-gray-200 rounded-full cursor-pointer transition-colors"
						disabled={disabled}
					>
						<ChevronRight className="w-4 h-4" aria-hidden="true" />
					</button>
				</TooltipTrigger>
				<TooltipContent>
					<p>Next</p>
				</TooltipContent>
			</Tooltip>
		</div>
	);
}
